/**
 * Composant MilestonesTimeline
 *
 * Affiche une timeline horizontale épurée des jalons du projet
 * Design moderne et professionnel avec gestion des chevauchements
 *
 * @param {Array} milestones - Liste des jalons
 */

import React, { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { format, endOfYear, eachMonthOfInterval, differenceInDays, startOfMonth, endOfMonth, addMonths, subMonths, min as minDate, max as maxDate } from 'date-fns';
import { fr } from 'date-fns/locale';

// Configuration des couleurs par statut
const STATUS_CONFIG = {
  completed: {
    label: 'Terminé',
    color: 'bg-green-500',
    borderColor: 'border-green-500',
    textColor: 'text-green-700',
  },
  inprogress: {
    label: 'En cours',
    color: 'bg-blue-500',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700',
  },
  upcoming: {
    label: 'À venir',
    color: 'bg-gray-400',
    borderColor: 'border-gray-400',
    textColor: 'text-gray-700',
  },
  overdue: {
    label: 'En retard',
    color: 'bg-red-500',
    borderColor: 'border-red-500',
    textColor: 'text-red-700',
  },
};

const MilestonesTimeline = ({ milestones = [], compact = false }) => {
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState(null);
  // Calculer la période adaptée aux jalons
  const { periodStart, periodEnd, displayMonths, today } = useMemo(() => {
    const now = new Date();

    // Filtrer les jalons valides
    const validMilestones = milestones.filter(m => m.date);

    if (validMilestones.length === 0) {
      // Par défaut, afficher l'année en cours à partir d'aujourd'hui
      return {
        periodStart: startOfMonth(now),
        periodEnd: endOfYear(now),
        displayMonths: eachMonthOfInterval({ start: startOfMonth(now), end: endOfYear(now) }),
        today: now
      };
    }

    // Trouver le premier et le dernier jalon
    const milestoneDates = validMilestones.map(m => new Date(m.date));
    const firstMilestone = minDate(milestoneDates);
    const lastMilestone = maxDate(milestoneDates);

    // Adapter la timeline aux jalons : commencer 1 mois avant le premier jalon
    const start = startOfMonth(compact ? firstMilestone : subMonths(firstMilestone, 1));
    // Terminer 1 mois après le dernier jalon
    const end = endOfMonth(compact ? lastMilestone : addMonths(lastMilestone, 1));

    return {
      periodStart: start,
      periodEnd: end,
      displayMonths: eachMonthOfInterval({ start, end }),
      today: now
    };
  }, [milestones, compact]);

  // Calculer la position d'un jalon sur la timeline (pourcentage)
  const getMilestonePosition = useCallback((milestoneDate) => {
    const date = new Date(milestoneDate);
    const daysFromStart = differenceInDays(date, periodStart);
    const totalDays = differenceInDays(periodEnd, periodStart);
    return Math.max(0, Math.min(100, (daysFromStart / totalDays) * 100));
  }, [periodStart, periodEnd]);

  // Position d'aujourd'hui sur la timeline
  const todayPosition = getMilestonePosition(today);

  // Calculer le délai en jours jusqu'à un jalon
  const getDaysUntilMilestone = (milestoneDate) => {
    return differenceInDays(new Date(milestoneDate), today);
  };

  // Tous les jalons valides avec leur position
  const milestonesWithPosition = useMemo(() => {
    const validMilestones = milestones.filter(m => m.date);

    // Calculer les positions
    const withPositions = validMilestones.map((milestone, index) => ({
      ...milestone,
      position: getMilestonePosition(milestone.date),
      originalIndex: index
    }));

    // Trier par position pour détecter les chevauchements
    withPositions.sort((a, b) => a.position - b.position);

    // Détecter les chevauchements et alterner haut/bas
    // Calculer un seuil dynamique basé sur la largeur estimée des labels
    const calculateOverlapThreshold = (milestone) => {
      // Estimer la largeur du label en caractères
      const labelLength = milestone.name.length;
      // Largeur approximative : 7px par caractère + 30px de padding/bordure
      const estimatedWidth = (labelLength * 7) + 30;
      // Convertir en pourcentage de la timeline (estimée à 1200px de large)
      // Ajouter une marge de sécurité de 50%
      return Math.max(12, (estimatedWidth / 1200) * 100 * 1.5);
    };

    // Niveaux disponibles pour chaque côté (top/bottom)
    const topLevels = ['top', 'top-high', 'top-high-2'];
    const bottomLevels = ['bottom', 'bottom-low', 'bottom-low-2'];

    // Garder trace des jalons récents pour détecter les chevauchements
    const recentMilestones = [];

    withPositions.forEach((milestone, index) => {
      // Calculer le seuil de chevauchement pour ce jalon
      const threshold = calculateOverlapThreshold(milestone);
      milestone.threshold = threshold;

      if (index === 0) {
        // Premier jalon : en haut par défaut
        milestone.row = 'top';
        milestone.side = 'top';
        milestone.levelIndex = 0;
      } else {
        // Nettoyer les jalons trop éloignés de la liste des récents
        const cleanedRecent = recentMilestones.filter(m => {
          // Utiliser le seuil le plus large entre les deux jalons
          const maxThreshold = Math.max(threshold, m.threshold);
          return (milestone.position - m.position) < maxThreshold;
        });

        if (cleanedRecent.length === 0) {
          // Aucun chevauchement : revenir en haut par défaut
          milestone.row = 'top';
          milestone.side = 'top';
          milestone.levelIndex = 0;
        } else {
          // Chevauchement détecté : alterner haut/bas
          const lastMilestone = cleanedRecent[cleanedRecent.length - 1];

          if (lastMilestone.side === 'top') {
            // Dernier était en haut → passer en bas
            milestone.side = 'bottom';
            // Trouver un niveau disponible en bas
            const usedBottomLevels = cleanedRecent
              .filter(m => m.side === 'bottom')
              .map(m => m.levelIndex);

            let levelIndex = 0;
            for (let i = 0; i < bottomLevels.length; i++) {
              if (!usedBottomLevels.includes(i)) {
                levelIndex = i;
                break;
              }
            }
            milestone.levelIndex = levelIndex;
            milestone.row = bottomLevels[levelIndex];
          } else {
            // Dernier était en bas → passer en haut
            milestone.side = 'top';
            // Trouver un niveau disponible en haut
            const usedTopLevels = cleanedRecent
              .filter(m => m.side === 'top')
              .map(m => m.levelIndex);

            let levelIndex = 0;
            for (let i = 0; i < topLevels.length; i++) {
              if (!usedTopLevels.includes(i)) {
                levelIndex = i;
                break;
              }
            }
            milestone.levelIndex = levelIndex;
            milestone.row = topLevels[levelIndex];
          }
        }

        // Mettre à jour la liste des jalons récents
        recentMilestones.length = 0;
        recentMilestones.push(...cleanedRecent, milestone);
      }
    });

    return withPositions;
  }, [milestones, getMilestonePosition]);

  if (compact && milestonesWithPosition.length === 0) {
    return <p className="horizon-empty" style={{ padding: '0 0 12px' }}>
      {milestones.length ? 'Aucune date prévue pour les jalons de ce projet.' : 'Aucun jalon défini pour ce projet.'}
    </p>;
  }

  // Si aucun jalon, afficher un message
  if (milestones.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
            <Calendar className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Timeline des Jalons</h2>
            <p className="text-xs text-gray-500">Aucun jalon</p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center py-8">
          <p className="text-sm text-gray-500 text-center">
            Aucun jalon défini pour ce projet
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "milestone-timeline-compact" : "bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col"}>
      {/* En-tête */}
      {compact ? <div className="milestone-timeline-meta">
        <span>{milestonesWithPosition.length} jalon{milestonesWithPosition.length > 1 ? 's' : ''} planifié{milestonesWithPosition.length > 1 ? 's' : ''}</span>
        <span>{format(periodStart, 'MMM yyyy', { locale: fr })}{displayMonths.length > 1 && ` – ${format(periodEnd, 'MMM yyyy', { locale: fr })}`}</span>
      </div> : <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Timeline des Jalons</h2>
              <p className="text-xs text-gray-500">{milestones.length} jalon{milestones.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          {/* Période affichée */}
          <div className="text-sm font-semibold text-gray-600">
            {format(periodStart, 'MMM yyyy', { locale: fr })} - {format(periodEnd, 'MMM yyyy', { locale: fr })}
          </div>
        </div>
      </div>}

      {/* Timeline horizontale */}
      <div className={compact ? "milestone-timeline-chart" : "p-6"}>
        {/* Container pour la timeline avec espace haut et bas (augmenté pour 6 niveaux) */}
        <div className="relative" style={{ paddingTop: compact ? '18px' : '40px', paddingBottom: compact ? (today >= periodStart && today <= periodEnd ? '48px' : '12px') : '40px' }}>
          {/* Ligne de timeline principale */}
          <div className="relative h-2 bg-gray-200 rounded-full overflow-visible">
            {/* Partie passée (avant aujourd'hui) */}
            <div
              className="absolute h-full bg-gradient-to-r from-gray-300 to-gray-200 rounded-full"
              style={{ width: `${todayPosition}%` }}
            ></div>

            {/* Point "Aujourd'hui" sur la timeline */}
            {(!compact || (today >= periodStart && today <= periodEnd)) && <div
              className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 z-20"
              style={{ left: `${todayPosition}%` }}
            >
              {/* Point sur la timeline */}
              <div className="w-3 h-3 bg-indigo-600 rounded-full border-2 border-white shadow-md"></div>

              {/* Label "Aujourd'hui" en dessous */}
              <div className="absolute top-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                <div className="text-xs font-bold text-indigo-600 text-center">
                  Aujourd'hui
                </div>
                <div className="text-xs text-gray-600 text-center mt-0.5">
                  {format(today, 'd MMM', { locale: fr })}
                </div>
              </div>
            </div>}

            {/* Jalons positionnés sur la ligne */}
            {milestonesWithPosition.map((milestone, index) => {
              const config = STATUS_CONFIG[milestone.status] || STATUS_CONFIG.upcoming;
              const daysUntil = getDaysUntilMilestone(milestone.date);
              const isFuture = daysUntil > 0;

              // Configuration des niveaux de positionnement (6 niveaux pour éviter chevauchements)
              const levelConfig = {
                'top-high-2': { isTop: true, lineHeight: 65, labelDistance: 85 },
                'top-high': { isTop: true, lineHeight: 50, labelDistance: 70 },
                'top': { isTop: true, lineHeight: 35, labelDistance: 50 },
                'bottom': { isTop: false, lineHeight: 35, labelDistance: 50 },
                'bottom-low': { isTop: false, lineHeight: 50, labelDistance: 70 },
                'bottom-low-2': { isTop: false, lineHeight: 65, labelDistance: 85 }
              };

              const { isTop, lineHeight, labelDistance } = levelConfig[milestone.row] || levelConfig['top'];

              const isHovered = hoveredId === (milestone.id || index);

              return (
                <div
                  key={milestone.id || index}
                  className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 z-10 cursor-pointer"
                  style={{ left: `${milestone.position}%` }}
                  onMouseEnter={() => setHoveredId(milestone.id || index)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => navigate('/dashboard/milestone-stories', { state: { milestoneId: milestone.id, milestoneName: milestone.name } })}
                >
                  {/* Marqueur (losange) sur la timeline */}
                  <div className={`${config.color} w-4 h-4 rotate-45 border-2 border-white shadow-md transition-transform duration-150 ${isHovered ? 'scale-125' : ''}`}></div>

                  {/* Ligne verticale vers le label (visible au hover) */}
                  {isHovered && (
                    <div
                      className={`absolute left-1/2 transform -translate-x-1/2 w-0.5 ${config.color}`}
                      style={{
                        [isTop ? 'bottom' : 'top']: '12px',
                        height: `${lineHeight}px`
                      }}
                    ></div>
                  )}

                  {/* Label et date (visible au hover) */}
                  {isHovered && (
                    <div
                      className="absolute left-1/2 transform -translate-x-1/2 whitespace-nowrap"
                      style={{
                        [isTop ? 'bottom' : 'top']: `${labelDistance}px`
                      }}
                    >
                      {/* Nom du jalon */}
                      <div className={`inline-block px-2 py-1 rounded-lg ${config.textColor} bg-white border-2 ${config.borderColor} shadow-sm font-semibold text-xs text-center`}>
                        {milestone.name}
                      </div>

                      {/* Date */}
                      <div className="text-xs text-gray-600 text-center mt-1 font-medium">
                        {format(new Date(milestone.date), 'd MMM', { locale: fr })}
                      </div>

                      {/* Délai si futur */}
                      {isFuture && (
                        <div className="text-xs text-gray-500 text-center mt-0.5">
                          dans {daysUntil}j
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mois de la période */}
        <div className="grid gap-0 text-xs text-gray-500 font-medium mt-4" style={{ gridTemplateColumns: `repeat(${displayMonths.length}, 1fr)` }}>
          {displayMonths.map((month, index) => (
            <div key={index} className="text-center">
              {format(month, 'MMM', { locale: fr })}
            </div>
          ))}
        </div>

        {/* Légende */}
        <div className={`flex flex-wrap gap-4 ${compact ? 'mt-3' : 'mt-6'}`}>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 bg-blue-500 rotate-45 border-2 border-white shadow"></div>
            <span className="text-gray-600 font-medium">En cours</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 bg-green-500 rotate-45 border-2 border-white shadow"></div>
            <span className="text-gray-600 font-medium">Terminé</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 bg-gray-400 rotate-45 border-2 border-white shadow"></div>
            <span className="text-gray-600 font-medium">À venir</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 bg-red-500 rotate-45 border-2 border-white shadow"></div>
            <span className="text-gray-600 font-medium">En retard</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MilestonesTimeline;
